// GPH Expert Intelligence Core — Game Expert Ontology (PDS §10).
// Base interna separada POR JOGO. Regra máxima: nunca misturar jogos.
// Usada por: intent resolver (vocabulário/subintents), coherence guard
// (categorias válidas), geração de dicas de query e fallback especialista.

export const GAME_LABELS = {
  pubg_mobile: 'PUBG Mobile',
  free_fire: 'Free Fire',
  codm: 'CODM'
};

const PUBG_MOBILE = {
  gameId: 'pubg_mobile',
  label: 'PUBG Mobile',
  aimTerms: ['recoil', 'spray', 'burst', 'ads', 'gyro', 'giroscopio', 'red dot', 'holo', 'acog', 'mira 3x', 'mira 4x', 'mira 6x', 'mira 8x', 'reset de recoil'],
  movementTerms: ['jiggle', 'peek', 'repeek', 'strafe', 'cover', 'quebrar angulo', 'reposicionamento', 'pincamento', 'pinçamento'],
  combatTerms: ['rush', 'clutch', '1v1', '1v2', '1v3', '1v4', 'x1', 'squad wipe', 'smoke', 'granada', 'utilitario', 'reload'],
  situations: {
    clutch_1v4: {
      label: 'ganhar 1v4',
      keywords: ['1v4', '1x4', 'sozinho contra squad', 'solo contra squad', 'clutch'],
      corePrinciple: 'Ganhar 1v4 não é matar quatro ao mesmo tempo. É transformar 1v4 em quatro lutas 1v1.',
      allowedCategories: ['duel_isolation', 'repositioning', 'angle_break', 'cover', 'utility', 'reload_discipline', 'timing', 'disengage']
    },
    vertical_spray: {
      label: 'controle de spray vertical',
      keywords: ['mira sobe', 'spray sobe', 'recoil vertical', 'sobe no spray'],
      corePrinciple: 'Recoil vertical se corrige com compensação constante e sensibilidade calibrada, não com força.',
      allowedCategories: ['recoil_control', 'spray_tracking', 'burst_control', 'recoil_reset', 'red_dot', 'gyro_ads', 'distance_control']
    }
  },
  scopes: ['red_dot', 'holo', '2x', '3x', '4x', '6x', '8x'],
  weapons: ['m416', 'groza', 'akm', 'beryl', 'ump', 'vector', 'dp28', 'scar', 'aug', 'famas', 'honey badger', 'kar98', 'm24', 'awm', 'mini14', 'sks', 'slr', 'mk14']
};

const FREE_FIRE = {
  gameId: 'free_fire',
  label: 'Free Fire',
  aimTerms: ['capa', 'puxada de mira', 'sensibilidade geral', 'red dot', 'mira 2x', 'mira 4x', 'awm', 'olhadinha', 'botao de tiro', 'botão de tiro'],
  movementTerms: ['gelo', 'movimentacao', 'movimentação', 'troca curta'],
  combatTerms: ['rush', 'combate de squad', 'posicionamento', 'x1'],
  situations: {
    capa_headshot: {
      label: 'capa/headshot',
      keywords: ['capa', 'headshot', 'puxada'],
      corePrinciple: 'A capa consistente vem de puxada curta e controlada, não de sensibilidade extrema.',
      allowedCategories: ['aim_pull', 'tracking', 'close_combat', 'movement']
    }
  },
  scopes: ['geral', 'red_dot', '2x', '4x', 'awm', 'olhadinha'],
  weapons: ['mp40', 'ump', 'm1014', 'awm', 'ak', 'scar', 'groza', 'm4a1', 'xm8']
};

const CODM = {
  gameId: 'codm',
  label: 'CODM',
  aimTerms: ['ads', 'hipfire', 'fov', 'sensibilidade', 'recoil', 'pre-aim', 'pre aim'],
  movementTerms: ['slide', 'slide cancel', 'strafe', 'jump shot'],
  combatTerms: ['mp ranqueada', 'ranked', 'br', 'smg', 'ar', 'sniper', 'close range', 'mid range', 'map control', 'respawn', 'objective', 'hardpoint', 'domination'],
  situations: {
    ranked_smg: {
      label: 'SMG close range ranqueada',
      keywords: ['smg', 'close range', 'ranked', 'ranqueada', 'mp'],
      corePrinciple: 'SMG em close range vence com pre-aim de ângulos e movimento imprevisível, não só com reflexo.',
      allowedCategories: ['pre_aim', 'movement', 'map_control', 'close_combat', 'tracking']
    }
  },
  scopes: ['red_dot', 'holo', '3x', '4x', 'sniper'],
  weapons: ['qq9', 'rus-79u', 'cordite', 'fennec', 'ak117', 'm4', 'dl q33', 'arctic']
};

const ONTOLOGIES = { pubg_mobile: PUBG_MOBILE, free_fire: FREE_FIRE, codm: CODM };

export const getOntology = (gameId) => ONTOLOGIES[gameId] || null;

export const getGameLabel = (gameId) => GAME_LABELS[gameId] || 'FPS Mobile';

// Detecta a situação conhecida mais próxima do texto, DENTRO do jogo dado.
export const matchSituation = (gameId, normalizedText) => {
  const ontology = getOntology(gameId);
  if (!ontology || !normalizedText) return null;
  for (const [key, situation] of Object.entries(ontology.situations)) {
    if (situation.keywords.some((kw) => normalizedText.includes(kw))) {
      return { key, ...situation };
    }
  }
  return null;
};

// Extrai arma citada no texto, respeitando o vocabulário do jogo.
export const matchWeapon = (gameId, normalizedText) => {
  const ontology = getOntology(gameId);
  if (!ontology || !normalizedText) return null;
  return ontology.weapons.find((w) => normalizedText.includes(w)) || null;
};

export default ONTOLOGIES;
